import {
  Box,
  Button,
  Flex,
  LoadingOverlay,
  Modal,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Device } from '@types';
import { pairDeviceSafe } from '../services/deviceManager';
import { getAvailableVrHeadsets } from '../utils/devicePairingLimits';

interface ConnectDeviceModalProps {
  opened: boolean;
  close: () => void;
  callback: (result: Device) => void;
  device: Device;
  devices: Device[];
  userId: string;
  onRefresh?: () => void;
}

interface FormValues {
  connected_to: string;
}

export default function ConnectDeviceModal({
  opened,
  close,
  callback,
  device,
  devices,
  userId,
  onRefresh,
}: ConnectDeviceModalProps) {
  const { t } = useTranslation();
  const [inProgress, setInProgress] = useState<boolean>(false);

  const form = useForm<FormValues>({
    initialValues: {
      connected_to: '',
    },
    validate: {
      connected_to: (value: string) =>
        !value ? 'Seleziona un dispositivo VR' : null,
    },
  });

  const submit = async (values: FormValues) => {
    if (!userId) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'Errore',
        autoClose: false,
        message: 'User ID è richiesto per abbinare il dispositivo',
      });
      return;
    }
    if (!values?.connected_to) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'Errore',
        autoClose: false,
        message: 'Seleziona un dispositivo VR a cui abbinare il sensore',
      });
      return;
    }
    setInProgress(true);
    try {
      const connectedTo =
        typeof values.connected_to === 'string'
          ? parseInt(values.connected_to, 10)
          : values.connected_to;

      const result = await pairDeviceSafe(userId, device.id.toString(), {
        connected_to: connectedTo,
      });
      if (result) {
        const connectedDeviceName =
          result.name ||
          result.model ||
          device.name ||
          device.model ||
          'Device';
        notifications.show({
          color: 'teal',
          withBorder: true,
          radius: 'lg',
          title: 'Connessione completata',
          autoClose: 2500,
          message: `${connectedDeviceName} connesso correttamente`,
        });
        callback(result);
        onRefresh?.();
      }
    } catch (error) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'An error occurred',
        autoClose: false,
        message: error instanceof Error ? error.message : 'Errore sconosciuto',
      });
    } finally {
      setInProgress(false);
    }
  };

  const vrHeadsetOptions = getAvailableVrHeadsets(devices).map(
    (d: Device) => ({
      label: d.name ?? '',
      value: d.id.toString(),
    })
  );

  const hasAvailableVr = vrHeadsetOptions.length > 0;

  return (
    <Modal opened={opened} onClose={close} title='Info' size={'lg'}>
      <form onSubmit={form.onSubmit(values => submit(values))}>
        <Box pos='relative'>
          <LoadingOverlay
            visible={inProgress}
            zIndex={1000}
            overlayProps={{ radius: 'sm', blur: 2 }}
          />
          <Stack>
            {!hasAvailableVr ? (
              <Text size='sm' c='dimmed'>
                Nessun visore disponibile: tutti i visori hanno già un sensore
                collegato, oppure non hai ancora aggiunto un visore.
              </Text>
            ) : (
              <Select
                data={vrHeadsetOptions}
                {...form.getInputProps('connected_to')}
                label={t('connect_to', 'Collega con')}
                required
              />
            )}
          </Stack>
        </Box>
        <Flex mt={'md'} justify={'flex-end'}>
          <Button
            type={'submit'}
            disabled={inProgress || !hasAvailableVr}
            loading={inProgress}
          >
            Collega
          </Button>
        </Flex>
      </form>
    </Modal>
  );
}
